import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  ArrowLeft,
  Bot,
  Check,
  Code2,
  Copy,
  GraduationCap,
  HelpCircle,
  Lightbulb,
  RotateCcw,
  Send,
  User,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatTokenCount } from "../constants/aiLimits";

const functions = getFunctions();
const askAIMentorGeneral = httpsCallable(functions, "askAIMentorGeneral");

const MODE_META = {
  learn: {
    label: "Learn",
    icon: GraduationCap,
    accent: "#22c55e",
    softAccent: "rgba(34, 197, 94, 0.18)",
    glow: "rgba(34, 197, 94, 0.22)",
    description: "Understand concepts clearly without overcomplicating them.",
    starter: "Explain this topic in simple words",
    prompts: [
      "Explain this topic in simple words",
      "Give me a beginner-friendly explanation",
      "What should I understand first before going deeper?",
    ],
  },
  code: {
    label: "Code",
    icon: Code2,
    accent: "#38bdf8",
    softAccent: "rgba(56, 189, 248, 0.18)",
    glow: "rgba(56, 189, 248, 0.24)",
    description: "Break down code problems into small, workable steps.",
    starter: "Help me solve this step by step",
    prompts: [
      "Help me solve this step by step",
      "Show me a clean example",
      "What should I code first?",
    ],
  },
  interview: {
    label: "Interview",
    icon: HelpCircle,
    accent: "#f59e0b",
    softAccent: "rgba(245, 158, 11, 0.18)",
    glow: "rgba(245, 158, 11, 0.24)",
    description: "Practice with structured questions and stronger answers.",
    starter: "Ask me an interview question on this topic",
    prompts: [
      "Ask me an interview question on this topic",
      "Give me a medium-level interview question",
      "How should I answer this confidently?",
    ],
  },
};

const MENTOR_VARIANTS = [
  {
    ids: [
      "frontend_mentor",
      "backend_role_mentor",
      "fullstack_mentor",
      "mobile_mentor",
      "devops_role_mentor",
      "data_role_mentor",
      "qa_role_mentor",
      "product_engineer_mentor",
      "startup_engineer_mentor",
      "remote_job_mentor",
    ],
    allowedModes: ["learn", "interview"],
    defaultMode: "learn",
    subtitle:
      "Get role clarity, skill direction, and hiring-focused guidance without unnecessary code-heavy answers.",
    modeOverrides: {
      learn: {
        label: "Path",
        description: "Understand what this role actually expects and what to focus on next.",
        starter: "What skills should I focus on for this role?",
        prompts: [
          "What skills should I focus on for this role?",
          "What should I learn first to become job-ready?",
          "What projects prove I am serious for this role?",
        ],
      },
      interview: {
        label: "Hiring",
        description: "Prepare for the hiring process, expectations, and stronger answers.",
        starter: "What kinds of interview questions come for this role?",
        prompts: [
          "What kinds of interview questions come for this role?",
          "How do I answer why I chose this role?",
          "What mistakes make candidates look weak for this role?",
        ],
      },
    },
  },
  {
    ids: [
      "dsa_mentor",
      "system_design_mentor",
      "frontend_interview_mentor",
      "backend_interview_mentor",
      "hr_round_mentor",
      "resume_mentor",
      "portfolio_mentor",
      "behavioral_mentor",
      "mock_interview_mentor",
      "salary_negotiation_mentor",
    ],
    allowedModes: ["interview", "learn"],
    defaultMode: "interview",
    subtitle:
      "Practice interview thinking, stronger answers, and focused preparation for the round you care about.",
    modeOverrides: {
      learn: {
        label: "Prep",
        description: "Understand the topic or round before jumping into questions.",
        starter: "What should I understand before practicing this round?",
        prompts: [
          "What should I understand before practicing this round?",
          "What does a strong answer usually include here?",
          "What do interviewers really check in this round?",
        ],
      },
      interview: {
        label: "Mock",
        description: "Practice questions, answer structure, and confidence under pressure.",
        starter: "Ask me a realistic interview question",
        prompts: [
          "Ask me a realistic interview question",
          "Give me a medium-level mock question",
          "How should I structure a strong answer?",
        ],
      },
    },
  },
  {
    ids: [
      "db_teacher",
      "os_teacher",
      "cn_mentor",
      "oop_mentor",
      "dbms_mentor",
      "compiler_mentor",
      "algo_mentor",
      "cn_systems_mentor",
      "security_fundamentals_mentor",
      "git_mentor",
    ],
    allowedModes: ["learn", "interview"],
    defaultMode: "learn",
    subtitle:
      "Build stronger fundamentals, understand the concepts clearly, and prepare for core-CS style questions.",
    modeOverrides: {
      learn: {
        label: "Concepts",
        description: "Understand the foundations clearly without overcomplicating them.",
        starter: "Explain this concept in simple words",
        prompts: [
          "Explain this concept in simple words",
          "What is the intuition behind this topic?",
          "What are the most important things to remember here?",
        ],
      },
      interview: {
        label: "Viva / Interview",
        description: "Practice theory-style, viva-style, and interview-style questions.",
        starter: "Ask me a core question on this topic",
        prompts: [
          "Ask me a core question on this topic",
          "Give me a viva-style question",
          "What follow-up questions usually come after this topic?",
        ],
      },
    },
  },
];

const getMentorVariant = (mentorId) =>
  MENTOR_VARIANTS.find((variant) => variant.ids.includes(mentorId)) || null;

const STARTER_MESSAGE = {
  role: "assistant",
  content:
    "I am here to help you learn with clarity, write better code, and prepare for interviews without getting overwhelmed.",
  meta: {
    type: "greeting",
    timestamp: Date.now(),
  },
};

const Page = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(680px at 12% 6%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(760px at 88% 10%, rgba(56, 189, 248, 0.14), transparent 62%),
    linear-gradient(180deg, rgba(8, 13, 24, 0.98), rgba(4, 8, 18, 0.98));
`;

const Header = styled.div`
  padding: 18px 18px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: rgba(2, 6, 23, 0.44);
  backdrop-filter: blur(12px);

  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const BackBtn = styled.button`
  height: 42px;
  width: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.5);
  color: #e2e8f0;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const HeaderBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 1000;
  line-height: 1.2;
`;

const Sub = styled.div`
  margin-top: 7px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
  max-width: 620px;
`;

const Presence = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #86efac;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
`;

const OnlineDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6);
  animation: pulse 1.6s infinite;

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const UtilityBar = styled.div`
  padding: 12px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  background: rgba(2, 6, 23, 0.26);

  @media (max-width: 640px) {
    padding: 12px 14px;
  }
`;

const UtilityBtn = styled.button`
  min-height: 38px;
  padding: 9px 13px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.46);
  color: #e2e8f0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
`;

const UsageWrap = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.2);

  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const UsageRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  font-size: 12px;
  color: #cbd5e1;
  margin-bottom: 8px;
`;

const UsageBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

const UsageFill = styled.div`
  height: 100%;
  width: ${({ percent }) => percent}%;
  background: linear-gradient(90deg, #22c55e, #38bdf8, #6366f1);
  transition: width 0.4s ease;
`;

const ModeBar = styled.div`
  padding: 12px 18px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.2);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 640px) {
    padding: 12px 14px;
  }
`;

const ModeBtn = styled.button`
  flex: 0 0 auto;
  min-height: 58px;
  padding: 10px 14px 10px 10px;
  border-radius: 18px;
  border: 1px solid
    ${({ active, $accent }) =>
      active ? $accent : "rgba(148, 163, 184, 0.14)"};
  background: ${({ active, $softAccent }) =>
    active
      ? `linear-gradient(135deg, ${$softAccent}, rgba(15, 23, 42, 0.9))`
      : "rgba(15, 23, 42, 0.46)"};
  color: ${({ active }) => (active ? "#f8fafc" : "#cbd5e1")};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: ${({ active, $glow }) =>
    active ? `0 16px 34px ${$glow}` : "none"};
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease,
    background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const ModeIconWrap = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ active, $softAccent }) =>
    active ? $softAccent : "rgba(30, 41, 59, 0.95)"};
  color: ${({ active, $accent }) => (active ? $accent : "#94a3b8")};
  border: 1px solid
    ${({ active, $accent }) => (active ? $accent : "rgba(148, 163, 184, 0.12)")};
`;

const ModeCopy = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
  min-width: 0;
`;

const ModeTitle = styled.span`
  font-size: 12px;
  font-weight: 900;
`;

const ModeDesc = styled.span`
  font-size: 10px;
  color: ${({ active }) =>
    active ? "rgba(248, 250, 252, 0.88)" : "rgba(226, 232, 240, 0.72)"};
  max-width: 180px;
  white-space: normal;
`;

const ChatWrapper = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PromptsScroller = styled.div`
  padding: 14px 18px 0;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 640px) {
    padding: 14px 14px 0;
  }
`;

const PromptChip = styled.button`
  flex: 0 0 auto;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.44);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  padding: 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;

  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const ChatRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  ${({ role }) => (role === "user" ? "flex-direction: row-reverse;" : "")}
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ role }) =>
    role === "user"
      ? "linear-gradient(135deg, #f59e0b, #facc15)"
      : "linear-gradient(135deg, #38bdf8, #6366f1)"};
  color: #04111f;
`;

const BubbleWrap = styled.div`
  max-width: min(82%, 620px);

  @media (max-width: 640px) {
    max-width: 88%;
  }
`;

const BubbleMeta = styled.div`
  margin: ${({ role }) => (role === "user" ? "0 4px 6px 0" : "0 0 6px 4px")};
  color: #64748b;
  font-size: 11px;
  display: flex;
  justify-content: ${({ role }) =>
    role === "user" ? "flex-end" : "flex-start"};
`;

const UserBubble = styled(motion.div)`
  padding: 13px 16px;
  border-radius: 22px 22px 8px 22px;
  line-height: 1.65;
  font-size: 14px;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  color: #04111f;
  box-shadow: 0 18px 40px rgba(245, 158, 11, 0.18);
`;

const MentorBubble = styled(motion.div)`
  padding: 16px 18px;
  border-radius: 22px 22px 22px 8px;
  line-height: 1.7;
  font-size: 14px;
  background: rgba(15, 23, 42, 0.76);
  color: #e5e7eb;
  border: 1px solid rgba(56, 189, 248, 0.16);
  backdrop-filter: blur(14px);
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.3);

  h4 {
    margin: 0 0 10px;
    font-size: 15px;
    color: #f8fafc;
  }

  p,
  pre,
  ul,
  ol,
  details {
    margin: 0 0 10px;
  }

  strong,
  summary {
    color: #f8fafc;
  }

  ul,
  ol {
    padding-left: 18px;
  }

  pre {
    overflow-x: auto;
    padding: 12px 14px;
    border-radius: 14px;
    background: rgba(2, 6, 23, 0.75);
    border: 1px solid rgba(148, 163, 184, 0.14);
  }
`;

const BubbleActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const ActionBtn = styled.button`
  border: 1px solid
    ${({ tone }) =>
      tone === "success"
        ? "rgba(34, 197, 94, 0.24)"
        : "rgba(56, 189, 248, 0.18)"};
  background: ${({ tone }) =>
    tone === "success"
      ? "rgba(34, 197, 94, 0.12)"
      : "rgba(56, 189, 248, 0.12)"};
  color: ${({ tone }) => (tone === "success" ? "#bbf7d0" : "#7dd3fc")};
  padding: 8px 12px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
`;

const Typing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin: 0 18px 12px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;

  @media (max-width: 640px) {
    margin: 0 14px 10px;
  }
`;

const TypingDots = styled.div`
  display: inline-flex;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #7dd3fc;
    animation: bounce 1s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.12s;
  }

  span:nth-child(3) {
    animation-delay: 0.24s;
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: translateY(0);
      opacity: 0.45;
    }
    40% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }
`;

const Composer = styled.div`
  padding: 10px 14px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.42);
  backdrop-filter: blur(14px);
  flex-shrink: 0;
`;

const ComposerShell = styled.div`
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.76);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  overflow: hidden;
`;

const Input = styled.textarea`
  width: 100%;
  resize: none;
  min-height: 56px;
  max-height: 110px;
  border: none;
  outline: none;
  background: transparent;
  color: #e2e8f0;
  padding: 12px 14px 6px;
  font-size: 14px;
  line-height: 1.6;

  &::placeholder {
    color: #64748b;
  }

  @media (max-width: 640px) {
    min-height: 52px;
  }
`;

const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 10px 10px 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ComposerHint = styled.div`
  color: #94a3b8;
  font-size: 12px;
`;

const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 640px) {
    justify-content: space-between;
    margin-left: 0;
  }
`;

const CharacterCount = styled.div`
  color: ${({ warn }) => (warn ? "#fca5a5" : "#64748b")};
  font-size: 12px;
  font-weight: 800;
`;

const SendBtn = styled.button`
  min-width: 96px;
  height: 42px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
  font-size: 14px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 16px 36px rgba(245, 158, 11, 0.2);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    box-shadow: none;
  }
`;

const Toast = styled(motion.div)`
  position: absolute;
  left: 50%;
  bottom: calc(94px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(56, 189, 248, 0.22);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 800;
  pointer-events: none;
  z-index: 4;
`;

const renderValue = (value) => {
  if (value == null) return null;

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((v, i) => <div key={i}>{renderValue(v)}</div>);
  }

  if (typeof value === "object") {
    return (
      <pre style={{ whiteSpace: "pre-wrap" }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return null;
};

const LearnCard = ({ data = {} }) => (
  <div>
    <h4>{renderValue(data.topic)}</h4>
    <p>{renderValue(data.explanation)}</p>

    {Array.isArray(data.examples) && data.examples.length > 0 && (
      <>
        <strong>Examples</strong>
        <ul>
          {data.examples.map((e, i) => (
            <li key={i}>{renderValue(e)}</li>
          ))}
        </ul>
      </>
    )}

    {Array.isArray(data.nextSteps) && data.nextSteps.length > 0 && (
      <>
        <strong>Next steps</strong>
        <ul>
          {data.nextSteps.map((s, i) => (
            <li key={i}>{renderValue(s)}</li>
          ))}
        </ul>
      </>
    )}
  </div>
);

const CodeCard = ({ data = {} }) => (
  <div>
    <h4>{renderValue(data.topic)}</h4>

    <pre>
      <code>{renderValue(data.code)}</code>
    </pre>

    {Array.isArray(data.steps) && data.steps.length > 0 && (
      <>
        <strong>Steps</strong>
        <ol>
          {data.steps.map((s, i) => (
            <li key={i}>{renderValue(s)}</li>
          ))}
        </ol>
      </>
    )}

    {Array.isArray(data.notes) && data.notes.length > 0 && (
      <>
        <strong>Notes</strong>
        <ul>
          {data.notes.map((n, i) => (
            <li key={i}>{renderValue(n)}</li>
          ))}
        </ul>
      </>
    )}
  </div>
);

const InterviewCard = ({ data = {} }) => (
  <div>
    <strong>Interview question ({renderValue(data.level)})</strong>
    <p>{renderValue(data.question)}</p>

    {Array.isArray(data.options) && data.options.length > 0 && (
      <ul>
        {data.options.map((o, i) => (
          <li key={i}>{renderValue(o)}</li>
        ))}
      </ul>
    )}

    <details>
      <summary>Show answer</summary>
      <p>
        <strong>{renderValue(data.correctAnswer)}</strong>
      </p>
      <p>{renderValue(data.explanation)}</p>
    </details>
  </div>
);

const getMessageTimestamp = (message, index) => {
  const stamp = message?.meta?.timestamp || message?.timestamp || Date.now() + index;
  return new Date(stamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const renderAssistantContent = (message) => {
  if (message.mode === "learn") return <LearnCard data={message.data} />;
  if (message.mode === "code") return <CodeCard data={message.data} />;
  if (message.mode === "interview") return <InterviewCard data={message.data} />;
  return renderValue(message.content);
};

export default function AIMentorChatPage({ onClose }) {
  const navigate = useNavigate();
  const { mentorId } = useParams();
  const { user } = useAuth();
  const inputRef = useRef(null);
  const messagesRef = useRef(null);

  const [mode, setMode] = useState("learn");
  const [messages, setMessages] = useState([STARTER_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copyState, setCopyState] = useState("");
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length, loading]);

  useEffect(() => {
    if (!copyState) return undefined;
    const timer = setTimeout(() => setCopyState(""), 1600);
    return () => clearTimeout(timer);
  }, [copyState]);

  const mentorName = useMemo(() => {
    if (mentorId) {
      return mentorId
        .split(/[-_]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    }

    return user?.roadmap?.claritySnapshot?.technology || "Career";
  }, [mentorId, user]);

  const mentorVariant = useMemo(() => getMentorVariant(mentorId), [mentorId]);

  const modeEntries = useMemo(() => {
    const allowedModes = mentorVariant?.allowedModes || Object.keys(MODE_META);

    return allowedModes.map((key) => {
      const base = MODE_META[key];
      const override = mentorVariant?.modeOverrides?.[key] || {};

      return [
        key,
        {
          ...base,
          ...override,
        },
      ];
    });
  }, [mentorVariant]);

  useEffect(() => {
    const defaultMode = mentorVariant?.defaultMode || "learn";
    const allowedModes = modeEntries.map(([key]) => key);

    if (!allowedModes.includes(mode)) {
      setMode(defaultMode);
    }
  }, [mentorVariant, mode, modeEntries]);
  const activeModeMeta =
    modeEntries.find(([key]) => key === mode)?.[1] || MODE_META.learn;
  const usagePercent = usage
    ? Math.min(Math.round((usage.used / usage.limit) * 100), 100)
    : 0;

  const sendMessage = async (messageOverride) => {
    const content = (messageOverride ?? input).trim();

    if (!content || loading) return;

    const userMessage = {
      role: "user",
      content,
      timestamp: Date.now(),
    };

    const history = messages.map(({ role, content: text }) => ({
      role,
      content: typeof text === "string" ? text : "",
    }));

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const res = await askAIMentorGeneral({
        mentorId,
        message: content,
        mode,
        history,
      });

      const { mode: responseMode, data, reply, usage: usageData } = res.data || {};

      if (usageData) {
        setUsage(usageData);
      }

      setMessages((prev) => [
        ...prev,
        responseMode
          ? {
              role: "assistant",
              mode: responseMode,
              data,
              meta: { timestamp: Date.now() },
            }
          : {
              role: "assistant",
              content: reply || "I could not generate a reply right now.",
              meta: { timestamp: Date.now() },
            },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            error?.details ||
            error?.message ||
            "Something went wrong. Please try again in a moment.",
          meta: { timestamp: Date.now() },
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(
        typeof content === "string" ? content : JSON.stringify(content, null, 2)
      );
      setCopyState("Copied reply");
    } catch {
      setCopyState("Copy failed");
    }
  };

  const resetChat = () => {
    setMessages([STARTER_MESSAGE]);
    setInput("");
    inputRef.current?.focus();
  };

  const handleBack = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }

    navigate(-1);
  };

  return (
    <Page>
      <Header>
        <BackBtn onClick={handleBack}>
          <ArrowLeft size={18} />
        </BackBtn>

        <HeaderBody>
          <HeaderTop>
            <div>
              <Title>{mentorName} AI Mentor</Title>
              <Sub>
                {mentorVariant?.subtitle ||
                  "Learn, code, and prepare for interviews with focused help that keeps you moving instead of overthinking."}
              </Sub>
            </div>

            <Presence>
              <OnlineDot />
              Available now
            </Presence>
          </HeaderTop>
        </HeaderBody>
      </Header>

      <UtilityBar>
        <UtilityBtn onClick={resetChat}>
          <RotateCcw size={14} />
          New chat
        </UtilityBtn>
        <UtilityBtn onClick={() => inputRef.current?.focus()}>
          <Lightbulb size={14} />
          Ask now
        </UtilityBtn>
      </UtilityBar>

      {usage && (
        <UsageWrap>
          <UsageRow>
            <span>Daily mentor usage</span>
            <span>
              {usage.used} / {usage.limit}
            </span>
          </UsageRow>

          <UsageBar>
            <UsageFill percent={usagePercent} />
          </UsageBar>

          <div style={{ fontSize: 11, color: "#64748b", marginTop: 7 }}>
            Resets at midnight. Current plan includes up to{" "}
            {formatTokenCount(usage.limit)} AI mentor tokens per day.
          </div>
        </UsageWrap>
      )}

      <ModeBar>
        {modeEntries.map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <ModeBtn
              key={key}
              active={mode === key}
              $accent={meta.accent}
              $softAccent={meta.softAccent}
              $glow={meta.glow}
              onClick={() => setMode(key)}
            >
              <ModeIconWrap
                active={mode === key}
                $accent={meta.accent}
                $softAccent={meta.softAccent}
              >
                <Icon size={16} />
              </ModeIconWrap>
              <ModeCopy>
                <ModeTitle>{meta.label}</ModeTitle>
                <ModeDesc active={mode === key}>{meta.description}</ModeDesc>
              </ModeCopy>
            </ModeBtn>
          );
        })}
      </ModeBar>

      <ChatWrapper>
        <ScrollArea>
          <PromptsScroller>
            {activeModeMeta.prompts.map((prompt) => (
              <PromptChip key={prompt} onClick={() => sendMessage(prompt)}>
                <Lightbulb size={14} />
                {prompt}
              </PromptChip>
            ))}
          </PromptsScroller>

          <Messages ref={messagesRef}>
            {messages.map((message, index) => (
              <ChatRow key={`${message.role}-${index}`} role={message.role}>
                <Avatar role={message.role}>
                  {message.role === "user" ? <User size={18} /> : <Bot size={18} />}
                </Avatar>

                <BubbleWrap>
                  <BubbleMeta role={message.role}>
                    {getMessageTimestamp(message, index)}
                  </BubbleMeta>

                  {message.role === "user" ? (
                    <UserBubble
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      {message.content}
                    </UserBubble>
                  ) : (
                    <MentorBubble
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      {renderAssistantContent(message)}

                      <BubbleActions>
                        <ActionBtn
                          tone="primary"
                          onClick={() =>
                            handleCopy(message.content || message.data || "")
                          }
                        >
                          <Copy size={13} />
                          Copy
                        </ActionBtn>

                        {message.mode && (
                          <ActionBtn
                            tone="success"
                            onClick={() =>
                              sendMessage(
                                `Give me the next step for this ${message.mode} topic`
                              )
                            }
                          >
                            <Check size={13} />
                            Next step
                          </ActionBtn>
                        )}
                      </BubbleActions>
                    </MentorBubble>
                  )}
                </BubbleWrap>
              </ChatRow>
            ))}

            {loading && (
              <Typing>
                <TypingDots>
                  <span />
                  <span />
                  <span />
                </TypingDots>
                Mentor is thinking...
              </Typing>
            )}
          </Messages>
        </ScrollArea>

        <Composer>
          <ComposerShell>
            <Input
              ref={inputRef}
              rows={1}
              placeholder={activeModeMeta.starter}
              value={input}
              maxLength={1200}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />

            <ComposerFooter>
              <ComposerHint>
                Press <strong>Enter</strong> to send, <strong>Shift + Enter</strong> for a new line.
              </ComposerHint>

              <ComposerActions>
                <CharacterCount warn={input.length > 1000}>
                  {input.length}/1200
                </CharacterCount>

                <SendBtn onClick={() => sendMessage()} disabled={loading || !input.trim()}>
                  <Send size={16} />
                  Send
                </SendBtn>
              </ComposerActions>
            </ComposerFooter>
          </ComposerShell>
        </Composer>
      </ChatWrapper>

      <AnimatePresence>
        {copyState && (
          <Toast
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {copyState}
          </Toast>
        )}
      </AnimatePresence>
    </Page>
  );
}
